import { useForm, useList, useOne } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import {
  Image as ImageIcon,
  RadioTower,
  Settings as SettingsIcon,
  Users,
  Languages as LanguagesIcon,
} from 'lucide-react';
import { LivestreamVisibility } from '@masters-club/types';
import { segmentDefinitionSchema, type SegmentDefinition } from '@masters-club/validation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  FormField,
  FormHeader,
  FormSpacer,
  FormStickyActions,
} from '@/components/forms';
import { ContentTagsField } from '@/components/shared';
import { FileImageUpload } from '@/components/upload/FileImageUpload';
import {
  TranslationsForm,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
} from '@/components/translations';
import {
  type Livestream,
  type LivestreamFormData,
  type LivestreamTranslation,
  LIVESTREAM_VISIBILITY_LABELS,
} from './types';
import { useFileInfo } from './hooks/use-file-info';
import { notify } from '@/lib/notify';
import { SegmentBuilder, AudienceUsersTable } from '@/components/audience-segment';
import { type Course, getCourseTitle } from '../courses/types';
import { getModuleTitle, getItemTitle, type Module } from '../modules/types';

// helper: нормализует audienceFilters (legacy или SegmentDefinition) → SegmentDefinition | null
function normalizeAudienceFilters(raw: Livestream['audienceFilters']): SegmentDefinition | null {
  if (raw === null || raw === undefined) { return null; }
  // Пробуем новый формат
  const parsed = segmentDefinitionSchema.safeParse(raw);
  if (parsed.success && parsed.data.groups.length > 0) {
    return parsed.data;
  }
  // Legacy — возвращаем null (UI не умеет его редактировать, пользователь задаст заново)
  return null;
}

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flagEmoji?: string | null;
  flag?: string | null;
}

const LIVESTREAM_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'title', label: 'Название', type: 'input', required: true, placeholder: 'Название трансляции' },
  { name: 'description', label: 'Описание', type: 'richtext', placeholder: 'Опционально' },
];

function translationsToData(translations: LivestreamTranslation[]): TranslationData {
  const data: TranslationData = {};
  for (const t of translations) {
    data[t.languageCode] = {
      title: t.title ?? '',
      description: t.description ?? '',
    };
  }
  return data;
}

function toFormState(livestream: Livestream | undefined): LivestreamFormData {
  return {
    visibility: livestream?.visibility ?? LivestreamVisibility.PRIVATE,
    scheduledAt: livestream?.scheduledAt ?? null,
    recordingEnabled: livestream?.recordingEnabled ?? false,
    maxViewersOverride: livestream?.maxViewersOverride ?? null,
    thumbnailFileId: livestream?.thumbnailFileId ?? null,
    languages: livestream?.languages ?? [],
    translations: livestream?.translations ?? [],
    audienceFilters: normalizeAudienceFilters(livestream?.audienceFilters ?? null),
    linkedModuleItemId: livestream?.linkedModuleItemId ?? null,
  };
}

interface LivestreamsFormProps {
  mode: 'create' | 'edit';
}

export function LivestreamsForm({ mode }: LivestreamsFormProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit';

  const { formLoading, onFinish, query } = useForm<Livestream>({
    resource: 'livestreams',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: 'list',
  });

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
    pagination: { pageSize: 100, mode: 'server' },
  });

  const languages: TranslationLanguage[] = (languagesResult?.data ?? []).map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName || l.name,
    flag: l.flag ?? l.flagEmoji ?? '🏳️',
  }));

  // Fallback, если backend ещё не прислал список языков.
  const effectiveLanguages: TranslationLanguage[] = languages.length > 0
    ? languages
    : [
      { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
      { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
      { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbekcha', flag: '🇺🇿' },
    ];

  const existing = query?.data?.data;
  const formKey = isEdit ? `edit-${existing?.id ?? 'loading'}` : 'create';

  return (
    <LivestreamsFormFields
      key={formKey}
      isEdit={isEdit}
      formLoading={formLoading}
      livestream={existing}
      livestreamId={isEdit && id ? Number(id) : undefined}
      availableLanguages={effectiveLanguages}
      onFinish={async (values) => {
        const payload = {
          visibility: values.visibility,
          scheduledAt: values.scheduledAt,
          recordingEnabled: values.recordingEnabled,
          maxViewersOverride: values.maxViewersOverride,
          thumbnailFileId: values.thumbnailFileId,
          languages: values.languages,
          translations: values.translations,
          audienceFilters:
            values.visibility === LivestreamVisibility.PRIVATE
              ? values.audienceFilters
              : null,
          linkedModuleItemId: values.linkedModuleItemId,
        };
        await onFinish(payload);
      }}
      onBack={() => navigate(-1)}
    />
  );
}

interface LivestreamsFormFieldsProps {
  isEdit: boolean;
  formLoading: boolean;
  livestream: Livestream | undefined;
  livestreamId?: number;
  locked?: boolean;
  availableLanguages: TranslationLanguage[];
  onFinish: (values: LivestreamFormData) => Promise<void>;
  onBack: () => void;
  showHeader?: boolean;
  showCancelAction?: boolean;
  submitLabel?: string;
}

export function LivestreamsFormFields({
  isEdit,
  formLoading,
  livestream,
  livestreamId,
  availableLanguages,
  onFinish,
  onBack,
  showHeader = true,
  showCancelAction = true,
  submitLabel,
}: LivestreamsFormFieldsProps) {
  const initial = toFormState(livestream);

  const [visibility, setVisibility] = useState<LivestreamVisibility>(initial.visibility);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(
    initial.scheduledAt ? new Date(initial.scheduledAt) : null,
  );
  const [recordingEnabled, setRecordingEnabled] = useState(initial.recordingEnabled);
  const [maxViewersOverride, setMaxViewersOverride] = useState<number | null>(
    initial.maxViewersOverride,
  );
  const [thumbnailFileId, setThumbnailFileId] = useState<number | null>(initial.thumbnailFileId);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');

  // Если при edit у эфира уже есть thumbnailFileId, подтягиваем url через /files/:id/info.
  useFileInfo(initial.thumbnailFileId, (info) => {
    if (info) {
      setThumbnailUrl(info.url);
    }
  });

  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(initial.languages);
  const [translationData, setTranslationData] = useState<TranslationData>(
    translationsToData(initial.translations),
  );

  const [audienceFilters, setAudienceFilters] = useState<SegmentDefinition | null>(
    initial.audienceFilters,
  );

  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(
    livestream?.linkedCourseId !== null && livestream?.linkedCourseId !== undefined
      ? String(livestream.linkedCourseId)
      : null,
  );
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(
    livestream?.linkedModuleId !== null && livestream?.linkedModuleId !== undefined
      ? String(livestream.linkedModuleId)
      : null,
  );
  const [selectedItemId, setSelectedItemId] = useState<string | null>(
    initial.linkedModuleItemId !== null && initial.linkedModuleItemId !== undefined ? String(initial.linkedModuleItemId) : null,
  );

  const { result: coursesResult } = useList<Course>({
    resource: 'courses',
    pagination: { pageSize: 200, mode: 'server' },
  });
  const courses = coursesResult?.data ?? [];

  const { result: selectedCourse } = useOne<Course>({
    resource: 'courses',
    id: selectedCourseId ?? '',
    queryOptions: { enabled: !!selectedCourseId },
  });
  const modules: Module[] = selectedCourse?.modules ?? [];
  const selectedModule = modules.find((m) => String(m.id) === selectedModuleId);
  const items = selectedModule?.items ?? [];

  const builtTranslations: LivestreamTranslation[] = useMemo(() => {
    return availableLanguages
      .map((lang) => {
        const entry = translationData[lang.code] ?? {};
        const title = String(entry.title ?? '').trim();
        const description = String(entry.description ?? '').trim();
        return {
          languageCode: lang.code,
          title,
          description: description.length > 0 ? description : null,
        };
      })
      .filter((t) => t.title.length > 0);
  }, [availableLanguages, translationData]);

  // Валидация: должен быть хотя бы один перевод с непустым title.
  const canSubmit = useMemo(() => {
    return builtTranslations.length > 0;
  }, [builtTranslations]);

  const handleSubmit = async () => {
    try {
      await onFinish({
        visibility,
        scheduledAt: scheduledAt ? scheduledAt.toISOString() : null,
        recordingEnabled,
        maxViewersOverride,
        thumbnailFileId,
        languages: selectedLanguages,
        translations: builtTranslations,
        audienceFilters,
        linkedModuleItemId: recordingEnabled && selectedItemId ? Number(selectedItemId) : null,
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : (error as { message?: string })?.message ?? 'Ошибка сохранения';
      notify.error(message);
    }
  };

  return (
    <div className="space-y-6">
      {showHeader && (
        <FormHeader
          title={isEdit ? 'Редактировать трансляцию' : 'Новая трансляция'}
          onBack={onBack}
          sticky
        />
      )}


      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          {/* Languages & Translations */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <LanguagesIcon className="h-4 w-4" />
                Языки и переводы
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <TranslationsForm
                fields={LIVESTREAM_TRANSLATION_FIELDS}
                value={translationData}
                onChange={setTranslationData}
                languages={availableLanguages}
              />

              <FormField
                label="Языковая аудитория"
                name="languages"
                hint="Пустое = виден всем. Выберите языки — эфир будет показан только пользователям этих языков."
              >
                <MultiSelect
                  options={availableLanguages.map((l) => ({
                    label: `${l.flag ? `${l.flag} ` : ''}${l.nativeName}`,
                    value: l.code,
                  }))}
                  value={selectedLanguages}
                  onChange={setSelectedLanguages}
                  placeholder="Виден всем"
                  searchPlaceholder="Поиск языка..."
                  emptyMessage="Языки не найдены"
                />
              </FormField>
            </CardContent>
          </Card>

          {/* Thumbnail */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Обложка-превью
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FileImageUpload
                value={thumbnailFileId ? { id: thumbnailFileId, url: thumbnailUrl } : null}
                onChange={(file) => {
                  setThumbnailFileId(file?.id ?? null);
                  setThumbnailUrl(file?.url ?? '');
                }}
                placeholder="Рекомендуемое соотношение сторон 16:9"
                aspectRatio="16/9"
              />
            </CardContent>
          </Card>

          {/* Audience filters — только для PRIVATE */}
          {visibility === LivestreamVisibility.PRIVATE && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Целевая аудитория
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <SegmentBuilder
                  value={audienceFilters ?? { groupsOp: 'AND', groups: [] }}
                  onChange={(next) => {
                    const groups = next.groups.filter((g) => g.conditions.length > 0);
                    setAudienceFilters(groups.length > 0 ? { ...next, groups } : null);
                  }}
                  disabled={formLoading}
                />

                {isEdit && livestreamId !== undefined ? (
                  <AudienceUsersTable
                    previewUrl={`/admin/livestreams/${livestreamId}/audience/preview`}
                    grantUrl={`/admin/livestreams/${livestreamId}/audience/grant`}
                    revokeUrl={`/admin/livestreams/${livestreamId}/audience/revoke`}
                    showAccessColumn
                    definition={audienceFilters ?? { groupsOp: 'AND', groups: [] }}
                    disabled={formLoading}
                  />
                ) : (
                  !isEdit && (
                    <p className="text-xs text-muted-foreground">
                      Сохраните трансляцию — после этого сможете точечно выдавать доступ пользователям.
                    </p>
                  )
                )}
              </CardContent>
            </Card>
          )}

        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <RadioTower className="h-4 w-4" />
                Расписание
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Запланирована на" name="scheduledAt">
                <DateTimePicker
                  value={scheduledAt}
                  onChange={setScheduledAt}
                  placeholder="Без даты"
                                  />
              </FormField>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <SettingsIcon className="h-4 w-4" />
                Настройки
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Видимость" name="visibility">
                <Select
                  value={visibility}
                  onValueChange={(v) => setVisibility(v as LivestreamVisibility)}
                                  >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(LivestreamVisibility).map((v) => (
                      <SelectItem key={v} value={v}>
                        {LIVESTREAM_VISIBILITY_LABELS[v]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Запись эфира" name="recordingEnabled">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={recordingEnabled}
                    onCheckedChange={setRecordingEnabled}
                                      />
                  <span className="text-sm text-muted-foreground">
                    {recordingEnabled ? 'Запись включена' : 'Запись выключена'}
                  </span>
                </div>
              </FormField>

              {recordingEnabled && courses.length > 0 && (
                <FormField
                  label="Привязка записи к уроку"
                  name="linkedModuleItemId"
                  hint="Запись стрима будет сохранена как видео выбранного урока"
                >
                  <div className="space-y-2">
                    <Select
                      value={selectedCourseId ?? ''}
                      onValueChange={(v) => {
                        setSelectedCourseId(v || null);
                        setSelectedModuleId(null);
                        setSelectedItemId(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите курс" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={String(course.id)}>
                            {getCourseTitle(course)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {selectedCourseId && modules.length > 0 && (
                      <Select
                        value={selectedModuleId ?? ''}
                        onValueChange={(v) => {
                          setSelectedModuleId(v || null);
                          setSelectedItemId(null);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Выберите модуль" />
                        </SelectTrigger>
                        <SelectContent>
                          {modules.map((mod) => (
                            <SelectItem key={mod.id} value={String(mod.id)}>
                              {getModuleTitle(mod)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {selectedModuleId && items.length > 0 && (
                      <Select
                        value={selectedItemId ?? ''}
                        onValueChange={(v) => {
                          setSelectedItemId(v || null);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Выберите урок" />
                        </SelectTrigger>
                        <SelectContent>
                          {items.map((item) => (
                            <SelectItem key={item.id} value={String(item.id)}>
                              {getItemTitle(item)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {selectedItemId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedCourseId(null);
                          setSelectedModuleId(null);
                          setSelectedItemId(null);
                        }}
                      >
                        Отвязать
                      </Button>
                    )}
                  </div>
                </FormField>
              )}

              <FormField label="Лимит зрителей (override)" name="maxViewersOverride">
                <Input
                  type="number"
                  min={1}
                  max={10000}
                  value={maxViewersOverride ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      setMaxViewersOverride(null);
                      return;
                    }
                    const parsed = Number(v);
                    setMaxViewersOverride(Number.isFinite(parsed) ? parsed : null);
                  }}
                  placeholder="Без ограничения"
                                  />
              </FormField>
            </CardContent>
          </Card>

          {isEdit && livestreamId ? (
            <Card>
              <CardContent className="pt-6">
                <ContentTagsField entityType="LIVESTREAM" entityId={livestreamId} />
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      <FormSpacer />

      <FormStickyActions>
        <Button onClick={handleSubmit} disabled={formLoading || !canSubmit}>
          {submitLabel ?? (isEdit ? 'Сохранить' : 'Создать')}
        </Button>
        {showCancelAction && (
          <Button variant="outline" onClick={onBack}>
            Отмена
          </Button>
        )}
      </FormStickyActions>
    </div>
  );
}

export function LivestreamsCreate() {
  return <LivestreamsForm mode="create" />;
}

export function LivestreamsEdit() {
  return <LivestreamsForm mode="edit" />;
}
